import React, { useCallback, useContext, useMemo } from 'react'
import { Player } from 'player'
import { Context as ContextScala } from 'scala'
import { useEffectOnce } from 'react-use'
import { useRouter } from 'next/router'
import { Task } from '../../../../types'
import styles from './split.module.scss'
import { useData } from './use-data'
import { useInitialState } from './use-initial-state'
import { useSettings } from './use-settings'
import { useChannels } from './use-channels'
import { useController } from './use-controller'
import { usePersistedChannelSettings } from '../../../../hooks/player/use-persisted-channel-settings'
import { useDataExport } from './use-data-export'
import { useEventPlayer } from '../../../../hooks/player/use-event-player'
import { useDataStatusUpgradability } from './use-data-status'
import { useModalPaywall } from '../../../../hooks/event/use-modal-paywall'
import { ModalPaywall } from '../../../../components/modal-paywall'
import { useChangeOperations } from '../../../../hooks/tasks/use-upgradability-new/use-change-operations'
import { getTrackOperations } from '../../../../hooks/tasks/use-upgradability-new/operations-utils'
import { useModalInformation } from '../../../../hooks/tasks/use-modal-information'
import { ModalInformation } from '../../../../components/modal-information'
import { useInstrumentsSeparateCustom } from '../../../../hooks/use-instruments-separate-custom'
import { Context } from '../../../../context'
import { useFeatures } from './use-features'
import { useModalTimeLimitation } from '../../../../hooks/tasks/use-modal-time-limitation'
import { useEventMixer } from '../../../../hooks/player/use-event-mixer'

interface SplitTaskType extends Task {
  refetchTask(): void
  enableEditTask: boolean
  enableExport: boolean
}

export const SplitTask: React.FC<SplitTaskType> = (task) => {
  const { userToken } = useContext(Context)
  const { user } = useContext(ContextScala)
  const isPro = useMemo(() => !!user?.subscription?.isPro, [user])
  const isPremium = useMemo(() => !!user?.subscription?.isPremium, [user])
  const isFree = useMemo(() => user?.subscription?.plan === 'free', [user])
  const router = useRouter()
  const webTimeLimitPaywalls = useMemo(
    () => user?.featureFlags?.webTimeLimitPaywalls,
    [user?.featureFlags]
  )

  const {
    id: trackId,
    operations: allOperations,
    enableEditTask,
    enableExport
  } = task

  const summary = useMemo(() => task?.summary?.v1, [task])

  const { loadInstruments, instruments } = useInstrumentsSeparateCustom()

  const mainOperations = useMemo(
    () => getTrackOperations({ allOperations, trackId }),
    [allOperations, trackId]
  )

  const operationType = useMemo(
    () =>
      mainOperations?.find((i) => i.name.includes('SEPARATE'))?.params.type ||
      'custom-music-stems',
    [mainOperations]
  )

  const isHifi = useMemo(
    () => ['-hifi', 'custom-music-stems'].includes(operationType),
    [operationType]
  )

  const persistedSettings = usePersistedChannelSettings({
    trackId,
    watchChanges: false,
    operationType,
    summary: task?.summary?.v1
  })

  const modalPaywall = useModalPaywall()

  useModalTimeLimitation({
    modalPaywall,
    isFree,
    isPremium,
    isPro,
    duration: task?.file?.duration,
    trackId,
    enabled: webTimeLimitPaywalls
  })

  const modalInformation = useModalInformation({
    isPro,
    isHifi,
    duration: task?.file?.duration
  })

  const changeOperations = useChangeOperations({
    summary,
    trackId
  })

  useDataStatusUpgradability({
    summary: task?.summary?.v1
  })

  useDataExport({
    task,
    isHifi,
    mainOperations,
    persistedSettings
  })

  const { onEventDispatch } = useEventPlayer({
    operationType,
    operations: mainOperations,
    taskId: task?.id,
    isDemo: !!task?.isDemo,
    onGetChannelSettings: persistedSettings.onGetSettings,
    onOpenModalPremium: modalPaywall.onOpen
  })

  const { setTotalListenTime, totalListenTime, setStartTime, startTime } =
    useEventMixer({
      taskId: task?.id
    })

  const data = useData({
    task,
    // TODO: remove
    changeOperations
  })

  const channels = useChannels({
    enabled: !!user,
    summary: task?.summary?.v1,
    limited: !isPremium && !task?.isDemo
  })

  const settings = useSettings({
    task,
    mainOperations,
    enableEditTask,
    enableExport,
    data
  })

  const initialState = useInitialState({
    mainOperations,
    persistedSettings
  })
  const controller = useController({
    task,
    mainOperations,
    allOperations,
    persistedSettings,
    // TODO: remove
    changeOperations,
    modalPaywall,
    onEventDispatch,
    playback: {
      setTotalListenTime,
      totalListenTime,
      setStartTime,
      startTime
    }
  })

  const features = useFeatures({
    task,
    modalPaywall,
    changeOperations
  })

  const handleLoadInstruments = useCallback(() => {
    loadInstruments(userToken)
  }, [loadInstruments, userToken])

  useEffectOnce(() => {
    if (instruments.length === 0) {
      handleLoadInstruments()
    }
  })

  useEffectOnce(() => {
    const isSeparateCustom = !!mainOperations?.find(({ name }) => {
      return name?.includes('SEPARATE_CUSTOM')
    })

    if (isSeparateCustom) {
      router.replace(`${router.asPath}&custom=true`, undefined, {
        shallow: true
      })
    }
  })

  return (
    <div className={styles.container}>
      <Player
        data={data}
        channels={channels}
        settings={settings}
        initialState={initialState}
        controller={controller}
        features={features}
        // esse aqui tem que remover ------
        separate={task?.summary?.v1?.separate}
      />

      {modalPaywall.isOpen && <ModalPaywall {...modalPaywall} />}
      {modalInformation.show && <ModalInformation {...modalInformation} />}
    </div>
  )
}
